/* global reconciliationIndexUrl locationsUrl saveAllocationUrl dropAllocationUrl typeWorkforce typeNonWorkforce taxEnabled updateTaxUrl reconciliationTotalsUrl reconciliationSendForCommitmentUrl */
/* global refreshUrl filtersUrl editableGeneralInfo updateGeneralDataUrl */
/* global uploadUrl deleteAllUrl isFileRequired integrationConfigured syncEnabled action model toggleUrl syncNowUrl syncTypes confirmationUrl eligibleIdsUrl */
/* global bulkApplyPreviewUrl bulkApplyUrl bulkApplyUndoUrl archiveUrl restoreUrl */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';

import ToggleActualCostsSync from '../../integrations/activation/ToggleActualCostsSync';
import DeleteActualCosts from '../DeleteActualCosts';
import UploadHistoryTab from '../upload/UploadHistoryTab';

import ActualCostsAllocationTable from './ActualCostsAllocationTable';
import ArchivedActualCostsTable from './ArchivedActualCostsTable';
import ArchiveTransactionModal from './ArchiveTransactionModal';
import ActualsDashboard from './dashboard/ActualsDashboard';
import SendForCommitmentQueue from './SendForCommitmentQueue';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableFilterModal from 'CommonComponents/TableFilterModal';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const TAB_DASHBOARD = 'dashboard';
const TAB_UNRECONCILED = 'unreconciled';
const TAB_RECONCILED = 'reconciled';
const TAB_SEND_FOR_COMMITMENT = 'send_for_commitment';
const TAB_ARCHIVED = 'archived';
const TAB_UPLOAD_HISTORY = 'upload_history';

const TYPE_ALL = 'all';
const TYPE_REVENUE = 'revenue';
const TYPE_EXPENDITURE = 'expenditure';

const BADGE_MAX = 999;

/**
 * Where the "bulk apply exists" hint records that it has been dismissed.
 *
 * Deliberately not scoped to the event or the table: dismissing it is a statement
 * about the reconciler, not about one list, and filtering is the main way this
 * screen is used — a per-table key would ask the same person to dismiss the same
 * tip on every new event, twice.
 */
const BULK_APPLY_HINT_DISMISSED_KEY = 'reconciliation_bulk_apply_hint_dismissed_v1';

/**
 * Stand-in for the reconciled flag on the Archived tab.
 *
 * Archived rows keep whatever reconciled state they had, so the tab spans both
 * and cannot be addressed by that flag. Passing this sentinel wherever a
 * `reconciled` argument is expected gives the archived lists their own
 * pagination / filter / request slot without a parallel set of helpers.
 */
const SLOT_ARCHIVED = 'archived';

const isArchivedSlot = (reconciled) => reconciled === SLOT_ARCHIVED;

const slotKey = (reconciled) => (isArchivedSlot(reconciled) ? SLOT_ARCHIVED : reconciled ? 'reconciled' : 'unreconciled');

const emptyArchivedTotals = () => ({
    revenue: { number: 0, amount: 0 },
    expenditure: { number: 0, amount: 0 },
});

const defaultSearchParams = {
    supplier: '',
    documentType: '',
    glCode: '',
    department: '',
    budgetLine: '',
    contractReference: '',
    reason: '',
};

const defaultFilterParams = {
    supplier: [],
    documentType: [],
    glCode: [],
    department: [],
    budgetLine: [],
    contractReference: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// URL state helpers
// ─────────────────────────────────────────────────────────────────────────────

// The reconciliation page keeps its sub-tab and filter state in the URL so the
// view survives a refresh and can be deep-linked into directly (e.g.
// ?status=unreconciled&type=revenue). The sub-tab uses `status`.
//
// No `status` now means the Dashboard — the module's landing view. An existing
// link that carries one keeps landing exactly where it always did.
function readUrlState() {
    const params = new URLSearchParams(window.location.search);
    return {
        tab: params.get('status') || TAB_DASHBOARD,
        type: params.get('type') || TYPE_ALL,
        date: params.get('date') || '0',
        glCode: params.get('gl_code') || '0',
    };
}

function writeUrlState(state) {
    if (!window.history?.replaceState) {
        return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('status', state.tab);
    params.set('type', state.type);
    params.set('date', state.date);
    params.set('gl_code', state.glCode);
    const url = window.location.pathname + '?' + params.toString();
    window.history.replaceState({ path: url }, '', url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Badge helper
// ─────────────────────────────────────────────────────────────────────────────

// Header filters that hold a `{show, name}` object per option (see
// isFilterActive() in ActualCostsAllocationTable.js) rather than a raw boolean
// per option (supplier / glCode / documentType).
const SHOW_FLAG_FILTER_KEYS = ['department', 'budgetLine', 'contractReference'];

/**
 * Whether a header-filter value being written represents the filter being
 * CLEARED (inactive) rather than applied — mirrors isFilterActive()'s own rule
 * for each shape, so the two can never drift apart.
 *
 * "Show all" then Apply sends every option back as a truthy/`show: true` entry
 * — a non-empty object, not `{}` — so testing only for emptiness treated a
 * just-cleared filter as freshly touched.
 */
function isFilterValueCleared(key, value) {
    if (value === null || value === undefined || value === '') {
        return true;
    }

    if (typeof value !== 'object') {
        return false;
    }

    const entries = Object.values(value);

    if (entries.length === 0) {
        return true;
    }

    return SHOW_FLAG_FILTER_KEYS.includes(key) ? entries.every((entry) => entry.show !== false) : entries.every((entry) => !!entry);
}

function formatBadgeCount(count) {
    if (count > BADGE_MAX) {
        return BADGE_MAX + '+';
    }
    return String(count);
}

function TabBadge({ count }) {
    return (
        <span
            className={classNames(
                'tw-ms-1.5 tw-inline-flex tw-items-center tw-justify-center',
                'tw-rounded-full tw-bg-charcoal-15 tw-text-charcoal tw-text-xs tw-font-medium',
                'tw-min-w-[1.25rem] tw-h-5 tw-px-1',
            )}
        >
            {formatBadgeCount(count)}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_MESSAGES = {
    [TAB_UNRECONCILED]: $t('No unreconciled items — nice work'),
    [TAB_RECONCILED]: $t('Nothing reconciled yet'),
    [TAB_SEND_FOR_COMMITMENT]: $t('Nothing flagged for commitment'),
    [TAB_ARCHIVED]: $t('Nothing archived'),
};

/**
 * Spelled out per tab rather than looked up from a map: the count has to be
 * interpolated, and a map of pre-translated strings would have to be run through
 * $t() a second time to do it — passing an Arabic string as a translation key.
 */
function hiddenItemsMessage(tab, count) {
    if (tab === TAB_RECONCILED) {
        return $t('{{count}} reconciled item(s) are hidden by the current filter', { count });
    }

    if (tab === TAB_ARCHIVED) {
        return $t('{{count}} archived item(s) are hidden by the current filter', { count });
    }

    return $t('{{count}} unreconciled item(s) are hidden by the current filter', { count });
}

/**
 * The tab's empty state, which has to distinguish "there is nothing left" from
 * "your filter is hiding everything that is left".
 *
 * Both read as an empty tab, because emptiness is measured from the FILTERED
 * total the listing came back with.
 *
 * `hiddenCount` is the unfiltered count for the tab, meaningful only because the
 * filtered total is zero — the one state this renders in. Three cases:
 *
 * - a number above zero: a filter is hiding that many, so say so;
 * - zero: nothing anywhere, so the cheerful message is the true one;
 * - null: a filter is narrowing the view but the figure behind it isn't known
 *   (still in flight, or its request failed). Never claim completion here.
 *
 * `filterActive` is separate from the count on purpose: where a filter is applied
 * and genuinely hiding nothing, the message is still "nice work", but the reader
 * is sitting on a filtered view and the way out belongs on screen either way.
 */
function EmptyState({ tab, filterActive = false, hiddenCount = 0, onClearFilters = null }) {
    const hiding = hiddenCount === null || hiddenCount > 0;

    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-text-charcoal-50 tw-gap-y-2">
            <p className="tw-text-base">{hiding ? $t('No items match the current filter') : EMPTY_MESSAGES[tab] || $t('No items')}</p>
            {hiding && hiddenCount !== null && <p className="tw-text-sm">{hiddenItemsMessage(tab, hiddenCount)}</p>}
            {filterActive && typeof onClearFilters === 'function' && (
                <Button variant="secondary" onClick={onClearFilters}>
                    {$t('Clear filters')}
                </Button>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Archived summary header
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How much money has been excluded from the report, split revenue / expenditure.
 *
 * The headline number of the audit view: the question the Archived tab answers
 * is "what have we taken out, and what was it worth".
 */
function ArchivedSummary({ totals }) {
    const rows = [
        { key: TYPE_REVENUE, label: $t('Revenue'), figures: totals.revenue },
        { key: TYPE_EXPENDITURE, label: $t('Expenditure'), figures: totals.expenditure },
    ];

    return (
        <div className="table__card tw-mb-4">
            <div className="tw-flex tw-flex-wrap tw-gap-x-10 tw-gap-y-3 tw-px-4 tw-py-3">
                {rows.map((row) => (
                    <div key={row.key} className="tw-flex tw-items-baseline tw-gap-x-2 tw-text-sm">
                        <span className="tw-font-semibold">{row.label}</span>
                        <span className="tw-text-charcoal-75">
                            {$t('{{count}} archived', { count: row.figures.number })} — {formatToFixedMoneyWithLocale(row.figures.amount)}
                        </span>
                    </div>
                ))}
                <div className="tw-flex tw-items-baseline tw-gap-x-2 tw-text-sm">
                    <span className="tw-font-semibold">{$t('Total')}</span>
                    <span className="tw-text-charcoal-75">
                        {$t('{{count}} archived', { count: totals.revenue.number + totals.expenditure.number })} —{' '}
                        {formatToFixedMoneyWithLocale(totals.revenue.amount + totals.expenditure.amount)}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Type filter sub-tabs
// ─────────────────────────────────────────────────────────────────────────────

function TypeFilter({ activeType, onChange }) {
    const types = [
        { id: TYPE_ALL, label: $t('All') },
        { id: TYPE_REVENUE, label: $t('Revenue') },
        { id: TYPE_EXPENDITURE, label: $t('Expenditure') },
    ];

    return (
        <div className="in-nav horizontal" role="tablist">
            {types.map((t) => (
                <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={activeType === t.id}
                    onClick={() => onChange(t.id)}
                    className={classNames('in-nav__item', activeType === t.id ? 'active' : 'inactive')}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function ReconciliationIndex(props) {
    // Read initial state from URL
    const initialUrlState = useMemo(() => readUrlState(), []);

    const [activeTab, setActiveTab] = useState(initialUrlState.tab);
    const [activeType, setActiveType] = useState(initialUrlState.type);
    const [dateFilter, setDateFilter] = useState(initialUrlState.date);
    const [glCodeFilter, setGlCodeFilter] = useState(initialUrlState.glCode);

    // The page-level Month / GL Code selection, which narrows every tab and every
    // table at once — unlike the per-table column filters. '0' means "all".
    const pageLevelFilterActive = dateFilter !== '0' || glCodeFilter !== '0';

    const [departments, setDepartments] = useState({ expenditure: [], revenue: [] });
    const [directLineItems, setDirectLineItems] = useState({});
    const [actualCostsReconciledExpenditure, setActualCostsReconciledExpenditure] = useState({});
    const [actualCostsReconciledRevenue, setActualCostsReconciledRevenue] = useState({});
    const [actualCostsUnreconciledExpenditure, setActualCostsUnreconciledExpenditure] = useState({});
    const [actualCostsUnreconciledRevenue, setActualCostsUnreconciledRevenue] = useState({});
    const [actualCostsArchivedExpenditure, setActualCostsArchivedExpenditure] = useState({});
    const [actualCostsArchivedRevenue, setActualCostsArchivedRevenue] = useState({});

    const [actualCostsTotals, setActualCostsTotals] = useState({
        revenue: {
            unreconciled: { number: 0, amount: 0 },
            reconciled: { number: 0, amount: 0 },
            send_for_commitment: { number: 0, amount: 0 },
            total: { number: 0, amount: 0 },
        },
        expenditure: {
            unreconciled: { number: 0, amount: 0 },
            reconciled: { number: 0, amount: 0 },
            send_for_commitment: { number: 0, amount: 0 },
            total: { number: 0, amount: 0 },
        },
        total: {
            unreconciled: { number: 0, amount: 0 },
            reconciled: { number: 0, amount: 0 },
            send_for_commitment: { number: 0, amount: 0 },
            total: { number: 0, amount: 0 },
        },
    });

    // The flagged allocations behind the Send for commitment tab. Fetched
    // alongside the totals — see fetchTotals — so the list and the badge counting
    // it are never refreshed out of step.
    const [sendForCommitmentItems, setSendForCommitmentItems] = useState([]);
    const [sendForCommitmentRequest, setSendForCommitmentRequest] = useState(false);

    // Month-wide item / reconciled counts for the month group headings, keyed by
    // actual-cost type. Piggy-backs on the totals endpoint, so it re-syncs after
    // every confirm without a fetch of its own.
    const [monthCounts, setMonthCounts] = useState({ expense: {}, revenue: {} });

    // Archived rows are hidden from `actualCostsTotals` by the backend's global
    // scope, so the Archived badge and its summary header get their own figures.
    const [archivedTotals, setArchivedTotals] = useState(emptyArchivedTotals());

    // The same figures with the page-level Month / GL Code selection cleared, or
    // null while none is applied (in which case the figures above already are the
    // unfiltered ones). Only an empty tab reads this — see hiddenCountFor().
    const [unfilteredTotals, setUnfilteredTotals] = useState(null);

    const [glCodes, setGlCodes] = useState({});
    const [revenueTaxes, setRevenueTaxes] = useState([]);
    const [expenditureTaxes, setExpenditureTaxes] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [eventId, setEventId] = useState(null);
    const [allContracts, setAllContracts] = useState({});
    const [types, setTypes] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState({});

    const [requestUnreconciledRevenue, setRequestUnreconciledRevenue] = useState(false);
    const [requestReconciledRevenue, setRequestReconciledRevenue] = useState(false);
    const [requestUnreconciledExpenditure, setRequestUnreconciledExpenditure] = useState(false);
    const [requestReconciledExpenditure, setRequestReconciledExpenditure] = useState(false);
    const [requestArchivedRevenue, setRequestArchivedRevenue] = useState(false);
    const [requestArchivedExpenditure, setRequestArchivedExpenditure] = useState(false);
    const [totalsRequest, setTotalsRequest] = useState(false);
    const [unfilteredTotalsRequest, setUnfilteredTotalsRequest] = useState(false);

    const [locationFetched, setLocationFetched] = useState(false);
    const [fetchActualCostsTrigger, setFetchActualCostsTrigger] = useState(0);

    const [filterDates, setFilterDates] = useState([]);
    const [filterCodes, setFilterCodes] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [locationsFailed, setLocationsFailed] = useState(false);

    // Which header filter the reconciler touched most recently, per table. Only
    // the bulk-apply panel reads it (to default its "rows matching" selector), and
    // only for the current visit — a reload resets it, as agreed.
    const [lastTouchedFilter, setLastTouchedFilter] = useState({});

    // Bumped per table (key `${type}:${slotKey}`) to tell it to clear its own
    // column checkboxes and search boxes — see clearColumnFilters().
    const [clearFiltersSignal, setClearFiltersSignal] = useState({});

    // Whether a table is currently showing its bulk-apply result bar, keyed by
    // `${type}:${reconciled ? 'reconciled' : 'unreconciled'}`. Reported by the table,
    // because the bar (and the action's only Undo) is its state — see
    // isUnreconciledTabEmpty / isReconciledTabEmpty.
    //
    // Keyed by table and not by type alone: both tabs can raise a bar for the same
    // type, and a bar on one tab must not suppress the other tab's empty state.
    const [bulkApplyResultShown, setBulkApplyResultShown] = useState({});

    const onBulkApplyResultChange = useCallback((type, shown, reconciled = false) => {
        const key = `${type}:${slotKey(reconciled)}`;

        setBulkApplyResultShown((previous) => ({ ...previous, [key]: shown }));
    }, []);

    // Whether a bulk apply's post-refresh filter check is still outstanding, per
    // actual-cost type. Only suppresses the empty state for the moment in between,
    // so the honest "N hidden · Clear filters" state does not appear for a frame
    // and then get cleared automatically underneath the reader.
    const [pendingFilterCheck, setPendingFilterCheck] = useState({});

    // Held here rather than in each table so that dismissing the hint on one
    // clears it from the other in the same breath — both unreconciled tables can
    // be filtered, and so can both be showing it.
    const [bulkApplyHintDismissed, setBulkApplyHintDismissed] = useState(() => {
        try {
            return localStorage.getItem(BULK_APPLY_HINT_DISMISSED_KEY) === '1';
        } catch (e) {
            // localStorage unavailable (private browsing, blocked storage) — fall
            // back to showing the hint rather than failing the whole page.
            return false;
        }
    });

    const dismissBulkApplyHint = useCallback(() => {
        try {
            localStorage.setItem(BULK_APPLY_HINT_DISMISSED_KEY, '1');
        } catch (e) {
            // localStorage unavailable — fall back to in-memory only, so the
            // dismissal at least holds for the rest of this visit.
        }
        setBulkApplyHintDismissed(true);
    }, []);

    const [collapsed, setCollapsed] = useState({
        unreconciledRevenue: true,
        unreconciledExpenditure: true,
        reconciledRevenue: true,
        reconciledExpenditure: true,
        archivedRevenue: true,
        archivedExpenditure: true,
    });

    // Archive prompt: the row awaiting confirmation, and whether its POST is in
    // flight. Restore has no prompt, so it only tracks the ids mid-request.
    const archiveModal = useModal();
    const [rowToArchive, setRowToArchive] = useState(null);
    const [archiving, setArchiving] = useState(false);
    const [restoringIds, setRestoringIds] = useState(new Set());

    // The frontend always sends an explicit per_page, so this default — not the
    // controller's fallback — is what a reconciler actually gets. It doubles
    // alongside the reduced row height, otherwise the compacted table would show
    // the same 20 rows with whitespace beneath them.
    const [paginationUnreconciledRevenue, setPaginationUnreconciledRevenue] = useState({ perPage: 40, page: 1, lastPage: 1, to: 1, from: 1, total: 0 });
    const [paginationReconciledRevenue, setPaginationReconciledRevenue] = useState({ perPage: 40, page: 1, lastPage: 1, to: 1, from: 1, total: 0 });
    const [paginationUnreconciledExpenditure, setPaginationUnreconciledExpenditure] = useState({ perPage: 40, page: 1, lastPage: 1, to: 1, from: 1, total: 0 });
    const [paginationReconciledExpenditure, setPaginationReconciledExpenditure] = useState({ perPage: 40, page: 1, lastPage: 1, to: 1, from: 1, total: 0 });
    const [paginationArchivedRevenue, setPaginationArchivedRevenue] = useState({ perPage: 40, page: 1, lastPage: 1, to: 1, from: 1, total: 0 });
    const [paginationArchivedExpenditure, setPaginationArchivedExpenditure] = useState({ perPage: 40, page: 1, lastPage: 1, to: 1, from: 1, total: 0 });

    const [filtersUnreconciledRevenue, setFiltersUnreconciledRevenue] = useState({
        filters: JSON.parse(JSON.stringify(defaultFilterParams)),
        search: JSON.parse(JSON.stringify(defaultSearchParams)),
    });
    const [filtersReconciledRevenue, setFiltersReconciledRevenue] = useState({
        filters: JSON.parse(JSON.stringify(defaultFilterParams)),
        search: JSON.parse(JSON.stringify(defaultSearchParams)),
    });
    const [filtersUnreconciledExpenditure, setFiltersUnreconciledExpenditure] = useState({
        filters: JSON.parse(JSON.stringify(defaultFilterParams)),
        search: JSON.parse(JSON.stringify(defaultSearchParams)),
    });
    const [filtersReconciledExpenditure, setFiltersReconciledExpenditure] = useState({
        filters: JSON.parse(JSON.stringify(defaultFilterParams)),
        search: JSON.parse(JSON.stringify(defaultSearchParams)),
    });
    const [filtersArchivedRevenue, setFiltersArchivedRevenue] = useState({
        filters: JSON.parse(JSON.stringify(defaultFilterParams)),
        search: JSON.parse(JSON.stringify(defaultSearchParams)),
    });
    const [filtersArchivedExpenditure, setFiltersArchivedExpenditure] = useState({
        filters: JSON.parse(JSON.stringify(defaultFilterParams)),
        search: JSON.parse(JSON.stringify(defaultSearchParams)),
    });

    const [tableFilters, setTableFilters] = useState({
        expense: { reconciled: {}, unreconciled: {}, archived: {} },
        revenue: { reconciled: {}, unreconciled: {}, archived: {} },
    });

    const requestsRef = useRef({});

    // fetchData is memoised WITHOUT pagination/filter state in its deps (so it
    // keeps a stable identity and the page/per-page effects below don't re-fire
    // on every render). Reading that state directly inside fetchData would
    // therefore capture a STALE closure — it would always send the initial
    // page=1 / per_page=20 regardless of what the user selected. Mirror the live
    // state into a ref so getPagination/getFilters always read current values.
    //
    // The assignment lives in a useEffect rather than directly in the render body
    // so it is concurrent-safe under React 18: in concurrent mode a render can be
    // started and discarded, which would leave the ref holding state from an
    // abandoned render. A useEffect only runs after a render has been committed,
    // so the ref is only ever updated with state that actually made it to the DOM.
    // useEffect is sufficient (vs useLayoutEffect) because every read site
    // (getPagination / getFilters) is called from async callbacks — they always
    // run after the effect has fired for the current commit. (IB-698)
    const latestStateRef = useRef({});
    useEffect(() => {
        latestStateRef.current = {
            pagination: {
                revenue: { reconciled: paginationReconciledRevenue, unreconciled: paginationUnreconciledRevenue, archived: paginationArchivedRevenue },
                expense: {
                    reconciled: paginationReconciledExpenditure,
                    unreconciled: paginationUnreconciledExpenditure,
                    archived: paginationArchivedExpenditure,
                },
            },
            filters: {
                revenue: { reconciled: filtersReconciledRevenue, unreconciled: filtersUnreconciledRevenue, archived: filtersArchivedRevenue },
                expense: { reconciled: filtersReconciledExpenditure, unreconciled: filtersUnreconciledExpenditure, archived: filtersArchivedExpenditure },
            },
            // Read by the post-apply filter check, whose closure predates any sub-tab
            // switch the reconciler made while the apply was running.
            activeType,
        };
    }, [
        activeType,
        paginationReconciledRevenue,
        paginationUnreconciledRevenue,
        paginationReconciledExpenditure,
        paginationUnreconciledExpenditure,
        paginationArchivedRevenue,
        paginationArchivedExpenditure,
        filtersReconciledRevenue,
        filtersUnreconciledRevenue,
        filtersReconciledExpenditure,
        filtersUnreconciledExpenditure,
        filtersArchivedRevenue,
        filtersArchivedExpenditure,
    ]);
    const toast = useToast();

    // Memoised because the table folds these into the prop bundle it hands each
    // cell's bulk-apply panel; a fresh object per render would re-run the panel's
    // count fetch.
    const bulkApplyUrls = useMemo(
        () => ({
            preview: props.bulkApplyPreviewUrl,
            apply: props.bulkApplyUrl,
            undo: props.bulkApplyUndoUrl,
        }),
        [props.bulkApplyPreviewUrl, props.bulkApplyUrl, props.bulkApplyUndoUrl],
    );
    const confirm = useConfirmationModal();

    // Uploads only exist where the project is not fed by an ERP integration.
    const showUploadHistory = !props.integrationConfigured && !!props.uploadHistoryUrl;

    // ── URL state sync ──────────────────────────────────────────────────────
    // Skip the initial write so a deep-linked URL (e.g. ?status=reconciled&
    // type=revenue) stays clean until the user actually changes a sub-tab or
    // filter; thereafter every change is mirrored back to the URL.
    const urlStateInitialised = useRef(false);
    useEffect(() => {
        if (!urlStateInitialised.current) {
            urlStateInitialised.current = true;
            return;
        }
        writeUrlState({ tab: activeTab, type: activeType, date: dateFilter, glCode: glCodeFilter });
    }, [activeTab, activeType, dateFilter, glCodeFilter]);

    // ── Init ────────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchLocations().then(() => {
            fetchFilters();
            setLocationFetched(true);
            setFetchActualCostsTrigger((prev) => prev + 1);
        });
    }, []);

    useEffect(() => {
        return () => {
            Object.values(requestsRef.current).forEach((c) => {
                try {
                    c.abort();
                } catch (e) {
                    /* ignore */
                }
            });
        };
    }, []);

    useEffect(() => {
        if (locationFetched) {
            fetchAll();
            fetchTotals();
        }
    }, [fetchActualCostsTrigger]);

    // ── Re-fetch when type filter changes ───────────────────────────────────
    useEffect(() => {
        if (locationFetched) {
            fetchAll();
            fetchTotals();
        }
    }, [activeType]);

    // ── Pagination-triggered refetch (same as original component) ──────────
    useEffect(() => {
        fetchData('revenue', 1);
    }, [paginationReconciledRevenue.page, paginationReconciledRevenue.perPage]);
    useEffect(() => {
        fetchData('revenue', 0);
    }, [paginationUnreconciledRevenue.page, paginationUnreconciledRevenue.perPage]);
    useEffect(() => {
        fetchData('expense', 1);
    }, [paginationReconciledExpenditure.page, paginationReconciledExpenditure.perPage]);
    useEffect(() => {
        fetchData('expense', 0);
    }, [paginationUnreconciledExpenditure.page, paginationUnreconciledExpenditure.perPage]);
    useEffect(() => {
        fetchData('revenue', SLOT_ARCHIVED);
    }, [paginationArchivedRevenue.page, paginationArchivedRevenue.perPage]);
    useEffect(() => {
        fetchData('expense', SLOT_ARCHIVED);
    }, [paginationArchivedExpenditure.page, paginationArchivedExpenditure.perPage]);

    useEffect(() => {
        paginationReconciledRevenue.page === 1 ? fetchData('revenue', 1) : setPaginationPage('revenue', 1, 1);
    }, [filtersReconciledRevenue]);
    useEffect(() => {
        paginationUnreconciledRevenue.page === 1 ? fetchData('revenue', 0) : setPaginationPage('revenue', 0, 1);
    }, [filtersUnreconciledRevenue]);
    useEffect(() => {
        paginationReconciledExpenditure.page === 1 ? fetchData('expense', 1) : setPaginationPage('expense', 1, 1);
    }, [filtersReconciledExpenditure]);
    useEffect(() => {
        paginationUnreconciledExpenditure.page === 1 ? fetchData('expense', 0) : setPaginationPage('expense', 0, 1);
    }, [filtersUnreconciledExpenditure]);
    useEffect(() => {
        paginationArchivedRevenue.page === 1 ? fetchData('revenue', SLOT_ARCHIVED) : setPaginationPage('revenue', SLOT_ARCHIVED, 1);
    }, [filtersArchivedRevenue]);
    useEffect(() => {
        paginationArchivedExpenditure.page === 1 ? fetchData('expense', SLOT_ARCHIVED) : setPaginationPage('expense', SLOT_ARCHIVED, 1);
    }, [filtersArchivedExpenditure]);

    // ── Tab counts from totals ──────────────────────────────────────────────
    const unreconciledCount = actualCostsTotals.total.unreconciled.number;
    const reconciledCount = actualCostsTotals.total.reconciled.number;
    // Transactions with at least one flagged allocation, so it counts the same
    // things the other two badges do. The table below it is one row per flagged
    // ALLOCATION, so a split transaction makes the two differ — by design.
    const sendForCommitmentCount = actualCostsTotals.total.send_for_commitment?.number ?? 0;
    const archivedCount = archivedTotals.revenue.number + archivedTotals.expenditure.number;

    // ── Helpers (same as original component) ───────────────────────────────

    const toggleCollapsed = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const getDataToUpdate = (type, reconciled) => {
        if (isArchivedSlot(reconciled)) {
            return JSON.parse(JSON.stringify(type === 'revenue' ? actualCostsArchivedRevenue : actualCostsArchivedExpenditure));
        }
        if (type === 'revenue') {
            return reconciled ? JSON.parse(JSON.stringify(actualCostsReconciledRevenue)) : JSON.parse(JSON.stringify(actualCostsUnreconciledRevenue));
        }
        return reconciled ? JSON.parse(JSON.stringify(actualCostsReconciledExpenditure)) : JSON.parse(JSON.stringify(actualCostsUnreconciledExpenditure));
    };

    // Memoised, unlike its neighbours below, specifically so refreshAfterBulkApply
    // can list it as a dependency without picking up a new function identity —
    // and therefore a new refreshAfterBulkApply — on every render.
    const fetchFilters = useCallback(() => {
        return axios
            .get(props.filtersUrl)
            .then((response) => {
                setTableFilters(response.data.filters);
            })
            .catch((e) => {
                console.log(e);
            });
    }, [props.filtersUrl]);

    const fetchLocations = () => {
        return axios
            .get(props.locationsUrl)
            .then((res) => {
                const { data } = res;
                setExpenditureTaxes(data.expenditureTaxes);
                setRevenueTaxes(data.revenueTaxes);
                setEventId(data.eventId);
                setAllContracts(data.allContracts);

                const departmentsData = { expense: [], revenue: [] };
                const lineItemsData = {};

                data.departments.forEach((department) => {
                    const name = department.name;
                    const type = department.type;
                    const id = department.typed_id;

                    departmentsData[type].push({ name, id: department.id, type, typed_id: id });
                    lineItemsData[id] = { [props.typeWorkforce]: [], [props.typeNonWorkforce]: [] };

                    const datum = JSON.parse(JSON.stringify(department.directLineItems));
                    datum.forEach((item) => {
                        lineItemsData[id][item.type === props.typeWorkforce ? props.typeWorkforce : props.typeNonWorkforce].push(item);
                    });

                    if ('costcenters' in department) {
                        department.costcenters.forEach((cc) => {
                            const ccId = cc.typed_id;
                            departmentsData[type].push({ name: department.name + ' / ' + cc.name, id: cc.id, type, typed_id: ccId });
                            lineItemsData[ccId] = { [props.typeWorkforce]: [], [props.typeNonWorkforce]: [] };
                            JSON.parse(JSON.stringify(cc.directLineItems)).forEach((item) => {
                                lineItemsData[ccId][item.type === props.typeWorkforce ? props.typeWorkforce : props.typeNonWorkforce].push(item);
                            });
                        });
                    }
                });

                setFilterDates(data.filterDates);
                setFilterCodes(data.filterCodes);
                setGlCodes(data.glCodes);
                setTypes(data.types);
                setSuppliers(data.suppliers);
                setTransactionTypes(data.transactionTypes);
                setDepartments(departmentsData);
                setDirectLineItems(lineItemsData);
                setFetched(true);
            })
            .catch((e) => {
                console.log(e);
                setLocationsFailed(true);
            });
    };

    const editActualCostGeneralInfoItem = (reconciled, group, month, index, key, value) => {
        let data = reconciled
            ? group === 'revenue'
                ? JSON.parse(JSON.stringify(actualCostsReconciledRevenue))
                : JSON.parse(JSON.stringify(actualCostsReconciledExpenditure))
            : group === 'revenue'
              ? JSON.parse(JSON.stringify(actualCostsUnreconciledRevenue))
              : JSON.parse(JSON.stringify(actualCostsUnreconciledExpenditure));

        if (data) {
            data[month][index][key] = value;
            setActualCostsGroupData(reconciled, group, data);
        }
    };

    const saveGeneralInfo = (reconciled, group, id, callback = null, callback2 = null) => {
        const opposite = reconciled ? 0 : 1;
        let data = reconciled
            ? group === 'revenue'
                ? JSON.parse(JSON.stringify(actualCostsReconciledRevenue))
                : JSON.parse(JSON.stringify(actualCostsReconciledExpenditure))
            : group === 'revenue'
              ? JSON.parse(JSON.stringify(actualCostsUnreconciledRevenue))
              : JSON.parse(JSON.stringify(actualCostsUnreconciledExpenditure));

        let item = null;
        Object.keys(data).forEach((month) => {
            data[month].forEach((row) => {
                if (row.id === id) item = row;
            });
        });

        if (item) {
            const url = props.updateGeneralDataUrl.replace('ACTUAL_COST_ID', item.id);
            const requestData = {
                transaction_description: item.transaction_description,
                amount: item.amount,
                document_type: item.document_type,
                document_number: item.document_number,
                date: item.date,
                gl_code: item.gl_code,
                supplier: item.supplier,
            };

            axios
                .put(url, requestData)
                .then(() => {
                    fetchData(group, reconciled).then(() => {
                        if (callback2 && typeof callback2 === 'function') callback2();
                    });
                    fetchData(group, opposite);
                    toast.success($t('Actual Costs updated successfully.'));
                })
                .catch((error) => {
                    toast.error(error.response?.data?.message || $t('Something went wrong.'));
                });
        }

        if (callback && typeof callback === 'function') callback();
    };

    const resetEdit = (reconciled, group, month, index, oldData) => {
        let data = reconciled
            ? group === 'revenue'
                ? JSON.parse(JSON.stringify(actualCostsReconciledRevenue))
                : JSON.parse(JSON.stringify(actualCostsReconciledExpenditure))
            : group === 'revenue'
              ? JSON.parse(JSON.stringify(actualCostsUnreconciledRevenue))
              : JSON.parse(JSON.stringify(actualCostsUnreconciledExpenditure));

        const item = data[month][index];
        Object.assign(item, {
            transaction_description: oldData.transaction_description,
            amount: oldData.amount,
            document_type: oldData.document_type,
            document_number: oldData.document_number,
            date: oldData.date,
            gl_code: oldData.gl_code,
            supplier_name: oldData.supplier_name,
            supplier: oldData.supplier,
        });

        data[month][index] = item;
        setActualCostsGroupData(reconciled, group, data);
    };

    const setActualCostsGroupData = (reconciled, group, data) => {
        if (isArchivedSlot(reconciled)) {
            group === 'revenue' ? setActualCostsArchivedRevenue(data) : setActualCostsArchivedExpenditure(data);
        } else if (reconciled) {
            group === 'revenue' ? setActualCostsReconciledRevenue(data) : setActualCostsReconciledExpenditure(data);
        } else {
            group === 'revenue' ? setActualCostsUnreconciledRevenue(data) : setActualCostsUnreconciledExpenditure(data);
        }
    };

    const setPaginationObj = (type, reconciled, data) => {
        if (isArchivedSlot(reconciled)) {
            type === 'revenue' ? setPaginationArchivedRevenue(data) : setPaginationArchivedExpenditure(data);
        } else if (type === 'revenue') {
            reconciled ? setPaginationReconciledRevenue(data) : setPaginationUnreconciledRevenue(data);
        } else {
            reconciled ? setPaginationReconciledExpenditure(data) : setPaginationUnreconciledExpenditure(data);
        }
    };

    const getPagination = (type, reconciled) => {
        const current = latestStateRef.current.pagination[type][slotKey(reconciled)];
        return JSON.parse(JSON.stringify(current));
    };

    const isSamePagination = (a, b) =>
        a.total === b.total && a.lastPage === b.lastPage && a.from === b.from && a.to === b.to && a.perPage === b.perPage && a.page === b.page;

    const setPagination = (type, reconciled, paginationData) => {
        const current = getPagination(type, reconciled);
        const next = {
            ...current,
            total: paginationData.total,
            lastPage: paginationData.last_page,
            from: paginationData.from,
            to: paginationData.to,
            perPage: paginationData.per_page,
            page: paginationData.current_page,
        };
        if (!isSamePagination(current, next)) setPaginationObj(type, reconciled, next);
    };

    const setPaginationPage = (type, reconciled, page) => {
        const pagination = getPagination(type, reconciled);
        setPaginationObj(type, reconciled, { ...pagination, page });
    };

    const setPaginationPerPage = (type, reconciled, perPage) => {
        const pagination = getPagination(type, reconciled);
        setPaginationObj(type, reconciled, { ...pagination, perPage, page: 1 });
    };

    const setRequest = (type, reconciled, value) => {
        if (isArchivedSlot(reconciled)) {
            type === 'revenue' ? setRequestArchivedRevenue(value) : setRequestArchivedExpenditure(value);
        } else if (reconciled) {
            type === 'revenue' ? setRequestReconciledRevenue(value) : setRequestReconciledExpenditure(value);
        } else {
            type === 'revenue' ? setRequestUnreconciledRevenue(value) : setRequestUnreconciledExpenditure(value);
        }
    };

    const getFilters = (type, reconciled) => {
        return latestStateRef.current.filters[type][slotKey(reconciled)];
    };

    // Render-time reader for a slot's filter state. getFilters() above reads the
    // ref instead, which is right for the async fetch path and wrong here: the ref
    // is only assigned in an effect, so it lags the state this must reflect.
    const filtersStateFor = (type, reconciled) => {
        if (isArchivedSlot(reconciled)) {
            return type === 'revenue' ? filtersArchivedRevenue : filtersArchivedExpenditure;
        }

        if (reconciled) {
            return type === 'revenue' ? filtersReconciledRevenue : filtersReconciledExpenditure;
        }

        return type === 'revenue' ? filtersUnreconciledRevenue : filtersUnreconciledExpenditure;
    };

    const filtersSetterFor = (type, reconciled) => {
        if (isArchivedSlot(reconciled)) {
            return type === 'revenue' ? setFiltersArchivedRevenue : setFiltersArchivedExpenditure;
        }

        if (reconciled) {
            return type === 'revenue' ? setFiltersReconciledRevenue : setFiltersReconciledExpenditure;
        }

        return type === 'revenue' ? setFiltersUnreconciledRevenue : setFiltersUnreconciledExpenditure;
    };

    /**
     * Whether any column filter or search box is narrowing a listing.
     *
     * Reuses isFilterValueCleared() rather than restating the two filter shapes,
     * so this and the tables' own columnFilterActive cannot drift apart.
     *
     * Takes the filter object rather than looking it up, so an async caller can
     * hand it the ref's copy (getFilters) while render-time callers hand it state
     * (filtersStateFor) — the distinction that keeps the post-apply check off a
     * stale closure.
     */
    const hasActiveColumnFilters = (state) => {
        return (
            Object.entries(state?.filters ?? {}).some(([key, value]) => !isFilterValueCleared(key, value)) ||
            Object.values(state?.search ?? {}).some((value) => !!value)
        );
    };

    const applyFilters = (key, value, isFilter, type, reconciled) => {
        const group = isFilter ? 'filters' : 'search';
        const data = JSON.parse(JSON.stringify(filtersStateFor(type, reconciled)));
        const setter = filtersSetterFor(type, reconciled);

        data[group][key] = value;
        setter(data);

        // Mark the slot as mid-request in THIS commit, not the one after. The
        // refetch itself is only kicked off by the `useEffect` watching this
        // filter state, which — being passive — runs one commit later; without
        // this, `request` stays false for at least one painted frame while
        // `columnFilterActive` is already true, so the bulk-apply hint could
        // flash the pre-filter row count before correcting itself.
        setRequest(type, reconciled, true);

        // Only a filter being APPLIED sets the bulk-apply selector's default.
        // Clearing one (an emptied search box, an emptied selection) is the user
        // stepping away from that column, so it must not become the column the
        // panel then defaults to matching on.
        const cleared = isFilterValueCleared(key, value);

        if (!cleared) {
            // Key form is `${type}:${slotKey(...)}` — e.g. 'revenue:unreconciled'.
            // The four ActualCostsAllocationTable call sites read this map by
            // literal key; they must use the same form or the bulk-apply panel
            // silently loses the column the user filtered by.
            setLastTouchedFilter((previous) => ({ ...previous, [`${type}:${slotKey(reconciled)}`]: key }));
        }
    };

    /**
     * Clear every column filter and search on one table.
     *
     * Two halves, because the state is split: the payload the listing is fetched
     * with lives here, and the checkbox / search-box state behind each header icon
     * lives in the table. The signal counter tells the table to reset its half —
     * without it the icons would go inactive while the boxes inside still held the
     * old selection.
     */
    const clearColumnFilters = (type, reconciled) => {
        const slot = `${type}:${slotKey(reconciled)}`;

        const setter = filtersSetterFor(type, reconciled);

        setter({
            filters: JSON.parse(JSON.stringify(defaultFilterParams)),
            search: JSON.parse(JSON.stringify(defaultSearchParams)),
        });

        setRequest(type, reconciled, true);
        setClearFiltersSignal((previous) => ({ ...previous, [slot]: (previous[slot] ?? 0) + 1 }));

        // Same rule applyFilters() follows when a filter is cleared by hand: the
        // bulk-apply selector must not go on defaulting to a column nobody is
        // filtering by any more.
        setLastTouchedFilter((previous) => {
            if (!(slot in previous)) {
                return previous;
            }

            const next = { ...previous };
            delete next[slot];

            return next;
        });
    };

    const fetchAll = useCallback(
        (callback = null) => {
            fetchData('revenue', 0, callback);
            fetchData('expense', 0, callback);
            fetchData('revenue', 1, callback);
            fetchData('expense', 1, callback);
            fetchData('revenue', SLOT_ARCHIVED, callback);
            fetchData('expense', SLOT_ARCHIVED, callback);
        },
        [dateFilter, glCodeFilter, activeType],
    );

    const fetchData = useCallback(
        (type, rawReconciled, callback = null) => {
            const archived = isArchivedSlot(rawReconciled);

            // Normalised ONCE, here, rather than at each call site: callers pass
            // 0/1, a boolean, or the archived sentinel, and a raw boolean is
            // actively dangerous. URLSearchParams stringifies `false` to "false",
            // which survives the controller's `?: null` and then reads as TRUE
            // under PHP's `(bool) "false"` — the unreconciled fetch would return
            // the RECONCILED list and overwrite the unreconciled slot with it.
            // The same value keys the in-flight abort map below, so a mismatched
            // form ("expense:false" vs "expense:0") also lets two responses race
            // into one slot.
            const reconciled = archived ? SLOT_ARCHIVED : rawReconciled ? 1 : 0;

            const pagination = getPagination(type, reconciled);
            setRequest(type, reconciled, true);

            const url = new URL(props.indexUrl);
            const params = new URLSearchParams(url.search);
            params.set('type', type);
            // The archived listing ignores `reconciled` server-side, but the
            // parameter is not optional — send a settled value rather than the
            // sentinel.
            params.set('reconciled', archived ? 0 : reconciled);
            if (archived) {
                params.set('archived', 1);
            }
            params.set('date', dateFilter);
            params.set('gl_code', glCodeFilter);
            params.set('page', pagination.page);
            params.set('per_page', pagination.perPage);

            // Apply type filter (all / revenue / expenditure)
            if (activeType !== TYPE_ALL) {
                if (activeType === TYPE_REVENUE && type !== 'revenue') {
                    setRequest(type, reconciled, false);
                    return Promise.resolve();
                }
                if (activeType === TYPE_EXPENDITURE && type !== 'expense') {
                    setRequest(type, reconciled, false);
                    return Promise.resolve();
                }
            }

            const key = `${type}:${reconciled}`;
            try {
                requestsRef.current[key]?.abort();
            } catch (e) {
                /* ignore */
            }
            const controller = new AbortController();
            requestsRef.current[key] = controller;

            const payload = { filtersAndSearch: getFilters(type, reconciled) };

            return axios
                .post(props.indexUrl + '?' + params.toString(), payload, { signal: controller.signal })
                .then((res) => {
                    const { data } = res;
                    setPagination(type, reconciled, data.meta);
                    setActualCostsGroupData(reconciled, type, data.data);
                    if (callback && typeof callback === 'function') callback();

                    // Resolved with this fetch's own meta so a caller can decide from
                    // the numbers IT asked for rather than from the shared request
                    // flag, which says nothing about WHICH fetch has landed.
                    return data.meta;
                })
                .catch((e) => {
                    if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || e?.message === 'canceled') return;
                    console.log(e);
                })
                .finally(() => {
                    // Only the fetch that still owns the slot may say it is done.
                    // Nothing can be stranded true: a successor always sets the flag
                    // before aborting.
                    if (requestsRef.current[key] === controller) {
                        delete requestsRef.current[key];
                        setRequest(type, reconciled, false);
                    }
                });
        },
        [dateFilter, glCodeFilter, activeType],
    );

    // Abort-and-own, the same shape fetchData uses: this fires on every save,
    // confirm, archive and restore, so two quick actions overlap easily and the
    // older response would otherwise land last and overwrite the newer list.
    const fetchSendForCommitment = useCallback(() => {
        setSendForCommitmentRequest(true);
        const url = new URL(props.sendForCommitmentUrl);
        const params = new URLSearchParams(url.search);
        params.set('date', dateFilter);
        params.set('gl_code', glCodeFilter);
        const uri = url.origin + url.pathname + '?' + params.toString();

        const key = 'send-for-commitment';
        try {
            requestsRef.current[key]?.abort();
        } catch (e) {
            /* ignore */
        }
        const controller = new AbortController();
        requestsRef.current[key] = controller;

        return axios
            .get(uri, { signal: controller.signal })
            .then((res) => {
                // Only the fetch that still owns the slot may write to it.
                if (requestsRef.current[key] === controller) {
                    setSendForCommitmentItems(res.data.data ?? []);
                }
            })
            .catch((e) => {
                if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || e?.message === 'canceled') return;
                console.log(e);
            })
            .finally(() => {
                // Nothing can be stranded true: a successor sets the flag before
                // aborting its predecessor.
                if (requestsRef.current[key] === controller) {
                    delete requestsRef.current[key];
                    setSendForCommitmentRequest(false);
                }
            });
    }, [dateFilter, glCodeFilter, props.sendForCommitmentUrl]);

    const fetchTotals = useCallback(() => {
        // Ride the totals refresh rather than wiring a fetch of its own into every
        // save, confirm, archive and restore: same filters, same cadence, so the
        // tab and the badge above it cannot disagree about what is flagged.
        fetchSendForCommitment();

        setTotalsRequest(true);
        const url = new URL(props.totalsUrl);
        const params = new URLSearchParams(url.search);
        params.set('date', dateFilter);
        params.set('gl_code', glCodeFilter);
        const uri = url.origin + url.pathname + '?' + params.toString();

        return axios
            .get(uri)
            .then((res) => {
                const payload = {
                    totals: res.data.totals,
                    archivedTotals: res.data.archivedTotals ?? emptyArchivedTotals(),
                };

                setActualCostsTotals(payload.totals);
                setMonthCounts(res.data.monthCounts ?? {});
                setArchivedTotals(payload.archivedTotals);

                // Resolved with the payload for the same reason fetchData is — see
                // there.
                return payload;
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setTotalsRequest(false);
            });
    }, [dateFilter, glCodeFilter, fetchSendForCommitment]);

    /**
     * Clear the column filter when a bulk apply has emptied the filtered
     * unreconciled view but not the list behind it — otherwise the tab claims there
     * is nothing left to reconcile while the remainder sits hidden.
     *
     * Deliberately conditional. Clearing after every apply would throw away the
     * filter the reconciler is working through, and bulk apply is driven BY that
     * filter — they would have to re-apply it for each column.
     *
     * Decided from the two responses the apply itself asked for, not from the
     * shared per-slot request flags, which a mid-edit refetch overlapping the apply
     * can leave reading a stale pre-apply count.
     *
     * CONSTRAINT: this body must read nothing from state directly. It runs seconds
     * after the render that created it, from a callback memoised without it in its
     * deps, so a direct state read would be silently stale — and nothing here is
     * covered by a test. Arguments, refs (`latestStateRef`, `getFilters`), pure
     * functions of their arguments and React setters only.
     */
    const resolveFilterAfterBulkApply = (type, listing, totals) => {
        Promise.all([listing, totals])
            .then(([meta, payload]) => {
                setPendingFilterCheck((previous) => ({ ...previous, [type]: false }));

                // Aborted, skipped or failed: no trustworthy numbers to act on. The
                // empty state still tells the truth by itself, with a Clear button.
                if (!meta || !payload) {
                    return;
                }

                // The reconciler may have switched sub-tab while the apply ran. Clearing
                // the filter of a table they can no longer see — and announcing it — is
                // noise. Read through the ref: this closure predates the switch.
                const shown = latestStateRef.current.activeType;
                const ownType = type === 'revenue' ? TYPE_REVENUE : TYPE_EXPENDITURE;

                if (shown !== TYPE_ALL && shown !== ownType) {
                    return;
                }

                // Still work inside the filter — leave the reconciler where they are.
                if ((meta.total ?? 0) > 0) {
                    return;
                }

                if (!hasActiveColumnFilters(getFilters(type, 0))) {
                    return;
                }

                const figures = type === 'revenue' ? payload.totals?.revenue : payload.totals?.expenditure;
                const remaining = figures?.unreconciled?.number ?? 0;

                // Genuinely nothing left: the empty state is telling the truth, and
                // clearing a filter to reveal an empty table would only be confusing.
                if (remaining <= 0) {
                    return;
                }

                clearColumnFilters(type, 0);

                // Names the list, because with both on screen each has its own filter and
                // the reconciler would otherwise have to guess which one just moved.
                toast.success(
                    type === 'revenue'
                        ? $t('Revenue filter cleared — {{count}} unreconciled item(s) still to allocate', { count: remaining })
                        : $t('Expenditure filter cleared — {{count}} unreconciled item(s) still to allocate', { count: remaining }),
                );
            })
            .catch(() => {
                // Only reachable from a throw inside fetchData's or fetchTotals' own
                // `.finally`, both of which already `.catch` before it — but without
                // this, that path strands the flag true and permanently suppresses the
                // honest empty state for the rest of the visit.
                setPendingFilterCheck((previous) => ({ ...previous, [type]: false }));
            });
    };

    /**
     * A bulk apply can move rows between the two tabs (filling the last missing
     * column reconciles a row) and reaches rows on other pages, so nothing local
     * can be patched — both lists, the totals and the filter option lists are
     * re-read. Mirrors what confirmActualCosts does after a Confirm All.
     */
    const refreshAfterBulkApply = useCallback(
        (type, result = null) => {
            const listing = fetchData(type, 0);
            const totals = fetchTotals();

            fetchData(type, 1);
            fetchFilters();

            // Rows that reconciled have left the unreconciled listing, which can
            // leave the filtered view empty while plenty still sits behind the
            // filter. Only worth checking when some row actually moved — an apply
            // that reconciled nothing cannot have emptied anything. Undo passes no
            // result: it puts rows back, so the filter stays as it is.
            if ((result?.reconciledCount ?? 0) <= 0) {
                return;
            }

            setPendingFilterCheck((previous) => ({ ...previous, [type]: true }));
            resolveFilterAfterBulkApply(type, listing, totals);
        },
        [fetchData, fetchTotals, fetchFilters],
    );

    const processActualCostsAfterAllocation = (item, oldData, callback = null) => {
        const group = item.reconciled ? 'reconciled' : 'unreconciled';
        const oldGroup = oldData.reconciled ? 'reconciled' : 'unreconciled';
        const subGroup = item.type === 'revenue' ? 'revenue' : 'expenditure';

        let groupDatum = getDataToUpdate(subGroup, group === 'reconciled');
        let oldGroupDatum = getDataToUpdate(subGroup, oldGroup === 'reconciled');

        if (group === oldGroup) {
            groupDatum[item.month] = groupDatum[item.month].map((row) => (row.id === item.id ? item : row));
            setActualCostsGroupData(group === 'reconciled', subGroup, groupDatum);
        } else {
            oldGroupDatum[oldData.month] = oldGroupDatum[oldData.month].filter((row) => row.id !== item.id);
            setActualCostsGroupData(oldGroup === 'reconciled', subGroup, oldGroupDatum);

            const newMonthData = groupDatum[item.month] ?? [];
            newMonthData.push(item);
            groupDatum[item.month] = newMonthData;
            setActualCostsGroupData(group === 'reconciled', subGroup, groupDatum);

            // The optimistic move above does not touch pagination, but the tab
            // empty-states key off pagination.total — re-sync both lists so the
            // destination tab shows the moved item instead of its empty state.
            const fetchType = item.type === 'revenue' ? 'revenue' : 'expense';
            fetchData(fetchType, group === 'reconciled' ? 1 : 0);
            fetchData(fetchType, oldGroup === 'reconciled' ? 1 : 0);
        }

        fetchTotals();
        if (callback && typeof callback === 'function') callback();
    };

    // ── Archive / restore ───────────────────────────────────────────────────

    const fetchTypeOf = (row) => (row.type === 'revenue' ? 'revenue' : 'expense');

    const subGroupOf = (row) => (row.type === 'revenue' ? 'revenue' : 'expenditure');

    /**
     * Drop a row from one of the six lists without a reload.
     *
     * The list is patched in place so the row disappears the moment the request
     * succeeds, then re-fetched: the tab empty-states and the pagination footer
     * both read pagination.total, which an in-memory splice never touches.
     */
    const removeRowFromSlot = (row, reconciledSlot) => {
        const subGroup = subGroupOf(row);
        const data = getDataToUpdate(subGroup, reconciledSlot);

        if (data[row.month]) {
            data[row.month] = data[row.month].filter((item) => item.id !== row.id);
            if (data[row.month].length === 0) {
                delete data[row.month];
            }
        }

        setActualCostsGroupData(reconciledSlot, subGroup, data);
        fetchData(fetchTypeOf(row), reconciledSlot);
    };

    const requestArchive = (actualCost) => {
        setRowToArchive(actualCost);
        archiveModal.show();
    };

    const submitArchive = (reason) => {
        if (!rowToArchive) {
            return Promise.resolve();
        }

        const row = rowToArchive;
        setArchiving(true);

        return axios
            .post(props.archiveUrl.replace('ACTUAL_COST_ID', row.id), { reason: reason || null })
            .then(() => {
                removeRowFromSlot(row, !!row.reconciled);
                fetchData(fetchTypeOf(row), SLOT_ARCHIVED);
                fetchTotals();
                fetchFilters();
                // A template/pack child whose only allocation was just archived is
                // no longer "allocated" server-side (see forgetMemoizedAllocatedTemplatePackChildIds
                // in ActualCostsArchiveService::archive()) — refetch so it stops
                // looking stale in other rows' budget-line dropdowns.
                fetchLocations();
                toast.success($t('Transaction archived.'));
                archiveModal.hide();
                setRowToArchive(null);
            })
            .catch((e) => {
                toast.error(e.response?.data?.message || $t('The transaction could not be archived.'));
            })
            .finally(() => setArchiving(false));
    };

    const restoreActualCost = (row) => {
        const url = row.restoreUrl || props.restoreUrl.replace('ACTUAL_COST_ID', row.id);

        setRestoringIds((previous) => new Set([...previous, row.id]));

        return axios
            .post(url)
            .then((res) => {
                const dropped = res.data?.droppedBudgetLines ?? [];
                const fellBack = !!res.data?.fellBackToUnreconciled;
                const droppedTotal = res.data?.droppedAllocationsTotal ?? 0;

                removeRowFromSlot(row, SLOT_ARCHIVED);
                // Back to the tab it came from — unless every budget line it was
                // allocated to has since been deleted, which sends it to
                // Unreconciled instead.
                fetchData(fetchTypeOf(row), fellBack ? 0 : !!row.reconciled);
                fetchTotals();
                fetchFilters();

                if (fellBack) {
                    toast.warning(
                        $t('Transaction restored as unreconciled — every budget line it was allocated to has been deleted ({{amount}} unallocated).', {
                            amount: formatToFixedMoneyWithLocale(droppedTotal),
                        }),
                    );
                } else if (dropped.length > 0) {
                    toast.warning(
                        $t('Transaction restored. {{count}} allocation(s) worth {{amount}} were dropped — their budget line no longer exists: {{lines}}', {
                            count: dropped.length,
                            amount: formatToFixedMoneyWithLocale(droppedTotal),
                            lines: dropped.join(', '),
                        }),
                    );
                } else {
                    toast.success($t('Transaction restored.'));
                }
            })
            .catch((e) => {
                toast.error(e.response?.data?.message || $t('The transaction could not be restored.'));
            })
            .finally(() => {
                setRestoringIds((previous) => new Set([...previous].filter((id) => id !== row.id)));
            });
    };

    const resetFilters = () => {
        setDateFilter('0');
        setGlCodeFilter('0');
        setFetchActualCostsTrigger((prev) => prev + 1);
    };

    const setActualCostsItem = (group, type, month, index, row) => {
        const data = getDataToUpdate(type, group === 'reconciled');
        data[month][index] = row;
        setActualCostsGroupData(group === 'reconciled', type, data);
    };

    const combineRefreshUrl = (type) => {
        const url = new URL(props.refreshUrl);
        url.searchParams.set('type', type);
        return url.toString();
    };

    const postActualCostsConfirmations = (payload) => axios.post(props.confirmationUrl, payload);

    const confirmActualCosts = (payload, type, skippedCount = 0, isReconciled = false) => {
        return new Promise((resolve, reject) => {
            const pending = payload?.pending ?? [];
            const ids = payload?.ids ?? [];
            const totalItems = pending.length + ids.length;

            if (totalItems === 0) return resolve([]);

            const message =
                skippedCount > 0
                    ? $t(
                          'The fields can still be adjusted in the reconciled table after confirmation. {{skipped}} item(s) were skipped because they are missing required fields.',
                          { skipped: skippedCount },
                      )
                    : $t('The fields can still be adjusted in the reconciled table after confirmation.');

            confirm({
                title: $t('{{count}} Items to Confirm', { count: totalItems }),
                message,
                onConfirm: () => {
                    postActualCostsConfirmations({ pending, ids, reconciled: isReconciled })
                        .then((res) => {
                            const confirmed = res.data?.confirmed ?? [];
                            const skipped = res.data?.skipped ?? [];

                            fetchData(type, 0);
                            fetchData(type, 1);
                            fetchTotals();

                            if (confirmed.length > 0 && skipped.length === 0) {
                                toast.success($t('{{count}} item(s) confirmed successfully.', { count: confirmed.length }));
                            } else if (confirmed.length > 0 && skipped.length > 0) {
                                toast.success(
                                    $t('{{confirmed}} item(s) confirmed. {{skipped}} item(s) could not be confirmed automatically.', {
                                        confirmed: confirmed.length,
                                        skipped: skipped.length,
                                    }),
                                );
                            } else if (confirmed.length === 0 && skipped.length > 0) {
                                toast.warning(
                                    $t('No items were confirmed. {{skipped}} item(s) could not be confirmed automatically.', { skipped: skipped.length }),
                                );
                            }

                            resolve(confirmed);
                        })
                        .catch((e) => {
                            const msg = e.response?.data?.message || $t('Something went wrong during confirmation.');
                            toast.error(msg);
                            reject(new Error(msg));
                        });
                },
                onCancel: () => resolve(null),
            });
        });
    };

    // ── Tab content helpers ─────────────────────────────────────────────────

    const showRevenue = activeType === TYPE_ALL || activeType === TYPE_REVENUE;
    const showExpenditure = activeType === TYPE_ALL || activeType === TYPE_EXPENDITURE;

    // A bulk apply that reconciles everything the filter let through empties this
    // tab, and the empty state REPLACES the tables — unmounting them, and with them
    // the result bar holding the action's only Undo. So while a bar is up, or while
    // a post-apply filter check is still pending, the tables stay: an empty table
    // with an Undo above it beats a cheerful "nothing left" that loses it.
    const bulkApplyResultUp =
        (showRevenue && !!bulkApplyResultShown[`revenue:${slotKey(false)}`]) || (showExpenditure && !!bulkApplyResultShown[`expense:${slotKey(false)}`]);

    // The reconciled table's own bar. A contract reference applied across rows
    // filtered ON contract reference — "show me the reconciled rows with none", the
    // job this tab's bulk apply exists for — leaves every row outside the filter, so
    // this tab empties itself the same way and must hold its Undo the same way.
    const reconciledBulkApplyResultUp =
        (showRevenue && !!bulkApplyResultShown[`revenue:${slotKey(true)}`]) || (showExpenditure && !!bulkApplyResultShown[`expense:${slotKey(true)}`]);

    const filterCheckPending = (showRevenue && !!pendingFilterCheck.revenue) || (showExpenditure && !!pendingFilterCheck.expense);

    const isUnreconciledTabEmpty =
        !bulkApplyResultUp &&
        !filterCheckPending &&
        (!showRevenue || (!requestUnreconciledRevenue && paginationUnreconciledRevenue.total === 0)) &&
        (!showExpenditure || (!requestUnreconciledExpenditure && paginationUnreconciledExpenditure.total === 0));

    const isReconciledTabEmpty =
        !reconciledBulkApplyResultUp &&
        (!showRevenue || (!requestReconciledRevenue && paginationReconciledRevenue.total === 0)) &&
        (!showExpenditure || (!requestReconciledExpenditure && paginationReconciledExpenditure.total === 0));

    const isArchivedTabEmpty =
        (!showRevenue || (!requestArchivedRevenue && paginationArchivedRevenue.total === 0)) &&
        (!showExpenditure || (!requestArchivedExpenditure && paginationArchivedExpenditure.total === 0));

    const columnFiltersActiveOnTab = (reconciled) =>
        (showRevenue && hasActiveColumnFilters(filtersStateFor('revenue', reconciled))) ||
        (showExpenditure && hasActiveColumnFilters(filtersStateFor('expense', reconciled)));

    // Whether hiddenCountFor() below could actually use the unfiltered baseline —
    // both a page-level selection to measure against and a tab currently reading
    // empty from it. `fetchTotals` fires after every save, confirm and archive, but
    // this figure only matters while a tab is empty, so it is fetched separately
    // rather than on every one of those refreshes.
    const needsUnfilteredBaseline = pageLevelFilterActive && (isUnreconciledTabEmpty || isReconciledTabEmpty || isArchivedTabEmpty);

    useEffect(() => {
        if (!needsUnfilteredBaseline) {
            return;
        }

        setUnfilteredTotalsRequest(true);
        const url = new URL(props.totalsUrl);
        const params = new URLSearchParams(url.search);
        params.set('date', '0');
        params.set('gl_code', '0');

        axios
            .get(url.origin + url.pathname + '?' + params.toString())
            .then((res) =>
                setUnfilteredTotals({
                    totals: res.data.totals,
                    archivedTotals: res.data.archivedTotals ?? emptyArchivedTotals(),
                }),
            )
            .catch(() => setUnfilteredTotals(null))
            .finally(() => setUnfilteredTotalsRequest(false));
        // Re-checked whenever the filtered totals change, not just when a tab flips
        // empty — the baseline can otherwise go stale while the tab stays empty.
    }, [needsUnfilteredBaseline, actualCostsTotals, archivedTotals]);

    /**
     * How many items an empty tab is hiding behind a filter.
     *
     * A tab is only ever "empty" by its FILTERED total, so where it is empty the
     * unfiltered figure is exactly what is being held back. Two kinds of filter can
     * be doing it: the per-table column filters (which the tab's own totals ignore)
     * and the page-level Month / GL Code selection (which they don't) — hence the
     * separate baseline for the latter.
     *
     * Zero where no filter is active at all, so the empty state can never offer to
     * clear filters that aren't there. Null — "a filter is narrowing this, but the
     * figure behind it isn't known" — while the totals are in flight, so no stale
     * count from before the last save can flash, and if the baseline request failed.
     *
     * @param pick reads {revenue, expenditure} counts out of a totals payload.
     */
    const hiddenCountFor = (reconciled, pick) => {
        if (!columnFiltersActiveOnTab(reconciled) && !pageLevelFilterActive) {
            return 0;
        }

        if (pageLevelFilterActive ? unfilteredTotalsRequest : totalsRequest) {
            return null;
        }

        const baseline = pageLevelFilterActive ? unfilteredTotals : { totals: actualCostsTotals, archivedTotals };

        if (!baseline) {
            return null;
        }

        const figures = pick(baseline);
        const revenue = showRevenue ? figures.revenue : 0;
        const expenditure = showExpenditure ? figures.expenditure : 0;

        // A payload missing a group is unknown, not zero. Defaulting it to zero here
        // would turn a shape surprise into a congratulation — the one outcome the
        // rest of this function exists to prevent.
        if (revenue === undefined || revenue === null || expenditure === undefined || expenditure === null) {
            return null;
        }

        return revenue + expenditure;
    };

    /**
     * Clear everything narrowing the view the empty state is reporting on — both
     * the column filters and the page-level selection. Anything left behind would
     * make the button a lie: the count above it is measured against no filter at
     * all, so pressing it has to actually get there.
     */
    const clearAllFilters = (reconciled) => {
        const clearingRevenue = showRevenue && hasActiveColumnFilters(filtersStateFor('revenue', reconciled));
        const clearingExpenditure = showExpenditure && hasActiveColumnFilters(filtersStateFor('expense', reconciled));

        if (clearingRevenue) {
            clearColumnFilters('revenue', reconciled);
        }

        if (clearingExpenditure) {
            clearColumnFilters('expense', reconciled);
        }

        if (pageLevelFilterActive) {
            // resetFilters() refetches by bumping the trigger, which is an effect —
            // a commit later than this one. Mark the slots as requesting now, or the
            // tab paints once with the filters gone but the pre-clear counts still in
            // place, and announces that there is nothing left.
            if (showRevenue && !clearingRevenue) {
                setRequest('revenue', reconciled, true);
            }

            if (showExpenditure && !clearingExpenditure) {
                setRequest('expense', reconciled, true);
            }

            resetFilters();
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────

    return (
        <div className="tw-h-full tw-flex tw-flex-col tw-gap-y-4">
            <div className="tw-flex tw-flex-col tw-gap-y-4">
                {/* Tabs, with the page-level Month / G-L filter beside them. The filter drives
                    every tab's listing, not just the figures, so it stays reachable from all of
                    them rather than moving into the Dashboard's summary tile. Import and ERP sync
                    controls sit right-aligned on the same row and wrap below it on narrow screens. */}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <div className="in-nav horizontal !tw-w-auto tw-shrink-0" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === TAB_DASHBOARD}
                            aria-controls="reconciliation-tab-dashboard"
                            className={classNames('in-nav__item', activeTab === TAB_DASHBOARD ? 'active' : 'inactive')}
                            onClick={() => setActiveTab(TAB_DASHBOARD)}
                        >
                            {$t('Dashboard')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === TAB_UNRECONCILED}
                            aria-controls="reconciliation-tab-unreconciled"
                            className={classNames('in-nav__item', activeTab === TAB_UNRECONCILED ? 'active' : 'inactive')}
                            onClick={() => setActiveTab(TAB_UNRECONCILED)}
                        >
                            {$t('Unreconciled')}
                            <TabBadge count={unreconciledCount} />
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === TAB_RECONCILED}
                            aria-controls="reconciliation-tab-reconciled"
                            className={classNames('in-nav__item', activeTab === TAB_RECONCILED ? 'active' : 'inactive')}
                            onClick={() => setActiveTab(TAB_RECONCILED)}
                        >
                            {$t('Reconciled')}
                            <TabBadge count={reconciledCount} />
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === TAB_SEND_FOR_COMMITMENT}
                            aria-controls="reconciliation-tab-send-for-commitment"
                            className={classNames('in-nav__item', activeTab === TAB_SEND_FOR_COMMITMENT ? 'active' : 'inactive')}
                            onClick={() => setActiveTab(TAB_SEND_FOR_COMMITMENT)}
                        >
                            {$t('Send for commitment')}
                            <TabBadge count={sendForCommitmentCount} />
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === TAB_ARCHIVED}
                            aria-controls="reconciliation-tab-archived"
                            className={classNames('in-nav__item', activeTab === TAB_ARCHIVED ? 'active' : 'inactive')}
                            onClick={() => setActiveTab(TAB_ARCHIVED)}
                        >
                            {$t('Archived')}
                            <TabBadge count={archivedCount} />
                        </button>
                        {showUploadHistory && (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === TAB_UPLOAD_HISTORY}
                                aria-controls="reconciliation-tab-upload-history"
                                className={classNames('in-nav__item', activeTab === TAB_UPLOAD_HISTORY ? 'active' : 'inactive')}
                                onClick={() => setActiveTab(TAB_UPLOAD_HISTORY)}
                            >
                                {$t('Upload history')}
                            </button>
                        )}
                    </div>
                    <div className="tw-flex-shrink-0">
                        <TableFilterModal
                            id="reconciliation-table_filter-modal"
                            onCancel={resetFilters}
                            showCancel={true}
                            onApply={() => setFetchActualCostsTrigger((prev) => prev + 1)}
                        >
                            <div>
                                <label className="tw-block tw-text-xs tw-mb-2.5">{$t('GL Code')}:</label>
                                <select
                                    className="select select-input !tw-bg-white !tw-border"
                                    value={glCodeFilter}
                                    onChange={(e) => setGlCodeFilter(e.target.value)}
                                    style={{ paddingLeft: '8px', paddingBottom: '2px', paddingTop: '2px', paddingRight: '30px', fontSize: '12px' }}
                                >
                                    <option value={'0'}>{$t('All codes')}</option>
                                    {filterCodes.map((code) => (
                                        <option key={code.id} value={code.code}>
                                            {code.code + ': ' + code.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="tw-block tw-text-xs tw-mb-2.5">{$t('Month')}:</label>
                                <select
                                    className="select select-input !tw-bg-white !tw-border"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    style={{ paddingLeft: '8px', paddingBottom: '2px', paddingTop: '2px', paddingRight: '30px', fontSize: '12px' }}
                                >
                                    <option value={'0'}>{$t('All months')}</option>
                                    {filterDates.map((date) => (
                                        <option key={date} value={date}>
                                            {date}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </TableFilterModal>
                    </div>
                    <div className="tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                        {!props.integrationConfigured && (
                            <div className="tw-flex tw-items-center tw-gap-x-2">
                                <Button as="a" variant="primary" href={props.uploadUrl}>
                                    {$t('Import Actuals from Excel')}
                                </Button>
                                {!props.isFileRequired && <DeleteActualCosts url={props.deleteAllUrl} />}
                            </div>
                        )}
                        <ToggleActualCostsSync
                            integrationConfigured={props.integrationConfigured}
                            syncEnabled={props.syncEnabled}
                            action={props.action}
                            toggleUrl={props.toggleUrl}
                            model={props.model}
                            syncNowUrl={props.syncNowUrl}
                            toggleCallback={() => window.location.reload()}
                            syncTypes={props.syncTypes}
                        />
                    </div>
                </div>

                {/* Type filter row. Hidden on the Dashboard, which aggregates both types
                    itself, and on Send for commitment, which is expenditure-only: the listing
                    endpoint takes only the page-level Month / GL Code selection and filters to
                    ActualCost::TYPE_EXPENSE itself, so Revenue there could only ever read zero.
                    The selection is kept, not reset — the other tabs still honour it. */}
                {activeTab !== TAB_DASHBOARD && activeTab !== TAB_SEND_FOR_COMMITMENT && activeTab !== TAB_UPLOAD_HISTORY && (
                    <TypeFilter activeType={activeType} onChange={setActiveType} />
                )}

                <div id="reconciliation-tab-dashboard" role="tabpanel" hidden={activeTab !== TAB_DASHBOARD}>
                    {activeTab === TAB_DASHBOARD && (
                        <ActualsDashboard
                            dashboardUrl={props.dashboardUrl}
                            uploadUrl={props.uploadUrl}
                            totals={actualCostsTotals}
                            totalsLoading={totalsRequest || fetchActualCostsTrigger === 0}
                            onNavigate={(tab, type) => {
                                setActiveTab(tab);
                                if (type) {
                                    setActiveType(type);
                                }
                            }}
                        />
                    )}
                </div>

                {/* Upload history. Outside the `fetched` gate for the same reason as the
                    Dashboard: it loads its own list. */}
                {showUploadHistory && (
                    <div id="reconciliation-tab-upload-history" role="tabpanel" hidden={activeTab !== TAB_UPLOAD_HISTORY}>
                        {activeTab === TAB_UPLOAD_HISTORY && <UploadHistoryTab historyUrl={props.uploadHistoryUrl} uploadUrl={props.uploadUrl} />}
                    </div>
                )}

                {/* Loading spinner on initial fetch */}
                {!fetched && !locationsFailed && activeTab !== TAB_DASHBOARD && activeTab !== TAB_UPLOAD_HISTORY && <LoadingSpinner />}
                {locationsFailed && (
                    <p className="tw-my-6 tw-text-center tw-text-red-600">{$t('This page could not be loaded. Please reload to try again.')}</p>
                )}

                {/* Tab panels */}
                {fetched && (
                    <>
                        {/* Unreconciled tab */}
                        <div id="reconciliation-tab-unreconciled" role="tabpanel" hidden={activeTab !== TAB_UNRECONCILED}>
                            {activeTab === TAB_UNRECONCILED && isUnreconciledTabEmpty ? (
                                <EmptyState
                                    tab={TAB_UNRECONCILED}
                                    filterActive={columnFiltersActiveOnTab(0) || pageLevelFilterActive}
                                    hiddenCount={hiddenCountFor(0, (baseline) => ({
                                        revenue: baseline.totals?.revenue?.unreconciled?.number,
                                        expenditure: baseline.totals?.expenditure?.unreconciled?.number,
                                    }))}
                                    onClearFilters={() => clearAllFilters(0)}
                                />
                            ) : (
                                <>
                                    {showRevenue && (
                                        <ActualCostsAllocationTable
                                            glCodes={glCodes}
                                            departments={departments}
                                            directLineItems={directLineItems}
                                            data={_.isObject(actualCostsUnreconciledRevenue) ? actualCostsUnreconciledRevenue : {}}
                                            header={$t('Unreconciled Revenue')}
                                            saveAllocationUrl={saveAllocationUrl}
                                            fetchData={(callback) => fetchData('revenue', 0, callback)}
                                            fetchTotals={fetchTotals}
                                            request={requestUnreconciledRevenue}
                                            dropAllocationUrl={props.dropAllocationUrl}
                                            types={types}
                                            transactionTypes={transactionTypes}
                                            typeWorkforce={props.typeWorkforce}
                                            typeNonWorkforce={props.typeNonWorkforce}
                                            taxEnabled={props.taxEnabled}
                                            taxes={revenueTaxes}
                                            updateTaxUrl={props.updateTaxUrl}
                                            setActualCostsItem={(month, index, data) => setActualCostsItem('unreconciled', 'revenue', month, index, data)}
                                            toggleCollapsed={() => toggleCollapsed('unreconciledRevenue')}
                                            collapsed={collapsed.unreconciledRevenue}
                                            suppliers={suppliers}
                                            storeKey={eventId + '_unreconciled_revenues'}
                                            processActualCostsAfterAllocation={processActualCostsAfterAllocation}
                                            allContracts={allContracts}
                                            refreshUrl={combineRefreshUrl('revenue')}
                                            pagination={paginationUnreconciledRevenue}
                                            setPaginationPage={(page) => setPaginationPage('revenue', 0, page)}
                                            setPaginationPerPage={(perPage) => setPaginationPerPage('revenue', 0, perPage)}
                                            filters={filtersUnreconciledRevenue}
                                            setFilters={(key, value, isFilter) => applyFilters(key, value, isFilter, 'revenue', false)}
                                            tableFilters={tableFilters.revenue.unreconciled}
                                            clearColumnFiltersSignal={clearFiltersSignal['revenue:unreconciled']}
                                            actualCostType={'revenue'}
                                            isReconciled={false}
                                            fetchFilters={fetchFilters}
                                            editableGeneralInfo={editableGeneralInfo}
                                            editActualCostGeneralInfoItem={(month, index, key, value) =>
                                                editActualCostGeneralInfoItem(0, 'revenue', month, index, key, value)
                                            }
                                            resetEdit={(month, index, oldData) => resetEdit(0, 'revenue', month, index, oldData)}
                                            saveGeneralInfo={(id, callback) => saveGeneralInfo(0, 'revenue', id, callback)}
                                            toast={toast}
                                            confirmActualCosts={confirmActualCosts}
                                            eligibleIdsUrl={props.eligibleIdsUrl}
                                            dateFilter={dateFilter}
                                            glCodeFilter={glCodeFilter}
                                            monthCounts={monthCounts}
                                            bulkApplyUrls={bulkApplyUrls}
                                            bulkApplyHintDismissed={bulkApplyHintDismissed}
                                            onDismissBulkApplyHint={dismissBulkApplyHint}
                                            lastTouchedFilter={lastTouchedFilter['revenue:unreconciled']}
                                            onBulkApplied={refreshAfterBulkApply}
                                            onBulkApplyResultChange={onBulkApplyResultChange}
                                            archiveActualCost={requestArchive}
                                        />
                                    )}
                                    {showExpenditure && (
                                        <ActualCostsAllocationTable
                                            glCodes={glCodes}
                                            departments={departments}
                                            directLineItems={directLineItems}
                                            data={_.isObject(actualCostsUnreconciledExpenditure) ? actualCostsUnreconciledExpenditure : {}}
                                            header={$t('Unreconciled Expenditure')}
                                            saveAllocationUrl={saveAllocationUrl}
                                            fetchData={(callback) => fetchData('expense', 0, callback)}
                                            fetchTotals={fetchTotals}
                                            request={requestUnreconciledExpenditure}
                                            dropAllocationUrl={props.dropAllocationUrl}
                                            types={types}
                                            transactionTypes={transactionTypes}
                                            typeWorkforce={props.typeWorkforce}
                                            typeNonWorkforce={props.typeNonWorkforce}
                                            taxEnabled={props.taxEnabled}
                                            taxes={expenditureTaxes}
                                            updateTaxUrl={props.updateTaxUrl}
                                            setActualCostsItem={(month, index, data) => setActualCostsItem('unreconciled', 'expenditure', month, index, data)}
                                            toggleCollapsed={() => toggleCollapsed('unreconciledExpenditure')}
                                            collapsed={collapsed.unreconciledExpenditure}
                                            suppliers={suppliers}
                                            storeKey={eventId + '_unreconciled_expenditure'}
                                            processActualCostsAfterAllocation={processActualCostsAfterAllocation}
                                            allContracts={allContracts}
                                            refreshUrl={combineRefreshUrl('expense')}
                                            pagination={paginationUnreconciledExpenditure}
                                            setPaginationPage={(page) => setPaginationPage('expense', 0, page)}
                                            setPaginationPerPage={(perPage) => setPaginationPerPage('expense', 0, perPage)}
                                            filters={filtersUnreconciledExpenditure}
                                            setFilters={(key, value, isFilter) => applyFilters(key, value, isFilter, 'expense', false)}
                                            tableFilters={tableFilters.expense.unreconciled}
                                            clearColumnFiltersSignal={clearFiltersSignal['expense:unreconciled']}
                                            actualCostType={'expense'}
                                            isReconciled={false}
                                            fetchFilters={fetchFilters}
                                            editableGeneralInfo={editableGeneralInfo}
                                            editActualCostGeneralInfoItem={(month, index, key, value) =>
                                                editActualCostGeneralInfoItem(0, 'expenditure', month, index, key, value)
                                            }
                                            resetEdit={(month, index, oldData) => resetEdit(0, 'expenditure', month, index, oldData)}
                                            saveGeneralInfo={(id, callback) => saveGeneralInfo(0, 'expense', id, callback)}
                                            toast={toast}
                                            confirmActualCosts={confirmActualCosts}
                                            eligibleIdsUrl={props.eligibleIdsUrl}
                                            dateFilter={dateFilter}
                                            glCodeFilter={glCodeFilter}
                                            monthCounts={monthCounts}
                                            bulkApplyUrls={bulkApplyUrls}
                                            bulkApplyHintDismissed={bulkApplyHintDismissed}
                                            onDismissBulkApplyHint={dismissBulkApplyHint}
                                            lastTouchedFilter={lastTouchedFilter['expense:unreconciled']}
                                            onBulkApplied={refreshAfterBulkApply}
                                            onBulkApplyResultChange={onBulkApplyResultChange}
                                            archiveActualCost={requestArchive}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        {/* Reconciled tab */}
                        <div id="reconciliation-tab-reconciled" role="tabpanel" hidden={activeTab !== TAB_RECONCILED}>
                            {activeTab === TAB_RECONCILED && isReconciledTabEmpty ? (
                                <EmptyState
                                    tab={TAB_RECONCILED}
                                    filterActive={columnFiltersActiveOnTab(1) || pageLevelFilterActive}
                                    hiddenCount={hiddenCountFor(1, (baseline) => ({
                                        revenue: baseline.totals?.revenue?.reconciled?.number,
                                        expenditure: baseline.totals?.expenditure?.reconciled?.number,
                                    }))}
                                    onClearFilters={() => clearAllFilters(1)}
                                />
                            ) : (
                                <>
                                    {showRevenue && (
                                        <ActualCostsAllocationTable
                                            glCodes={glCodes}
                                            departments={departments}
                                            directLineItems={directLineItems}
                                            data={_.isObject(actualCostsReconciledRevenue) ? actualCostsReconciledRevenue : {}}
                                            header={$t('Reconciled Revenue')}
                                            saveAllocationUrl={saveAllocationUrl}
                                            fetchData={(callback) => fetchData('revenue', 1, callback)}
                                            fetchTotals={fetchTotals}
                                            request={requestReconciledRevenue}
                                            dropAllocationUrl={props.dropAllocationUrl}
                                            types={types}
                                            transactionTypes={transactionTypes}
                                            typeWorkforce={props.typeWorkforce}
                                            typeNonWorkforce={props.typeNonWorkforce}
                                            taxEnabled={props.taxEnabled}
                                            taxes={revenueTaxes}
                                            updateTaxUrl={props.updateTaxUrl}
                                            setActualCostsItem={(month, index, data) => setActualCostsItem('reconciled', 'revenue', month, index, data)}
                                            toggleCollapsed={() => toggleCollapsed('reconciledRevenue')}
                                            collapsed={collapsed.reconciledRevenue}
                                            suppliers={suppliers}
                                            storeKey={eventId + '_reconciled_revenue'}
                                            processActualCostsAfterAllocation={processActualCostsAfterAllocation}
                                            allContracts={allContracts}
                                            refreshUrl={combineRefreshUrl('revenue')}
                                            pagination={paginationReconciledRevenue}
                                            setPaginationPage={(page) => setPaginationPage('revenue', 1, page)}
                                            setPaginationPerPage={(perPage) => setPaginationPerPage('revenue', 1, perPage)}
                                            filters={filtersReconciledRevenue}
                                            setFilters={(key, value, isFilter) => applyFilters(key, value, isFilter, 'revenue', true)}
                                            tableFilters={tableFilters.revenue.reconciled}
                                            clearColumnFiltersSignal={clearFiltersSignal['revenue:reconciled']}
                                            actualCostType={'revenue'}
                                            isReconciled={true}
                                            fetchFilters={fetchFilters}
                                            editableGeneralInfo={editableGeneralInfo}
                                            editActualCostGeneralInfoItem={(month, index, key, value) =>
                                                editActualCostGeneralInfoItem(1, 'revenue', month, index, key, value)
                                            }
                                            resetEdit={(month, index, oldData) => resetEdit(1, 'revenue', month, index, oldData)}
                                            saveGeneralInfo={(id, callback) => saveGeneralInfo(1, 'revenue', id, callback)}
                                            toast={toast}
                                            confirmActualCosts={confirmActualCosts}
                                            eligibleIdsUrl={props.eligibleIdsUrl}
                                            dateFilter={dateFilter}
                                            glCodeFilter={glCodeFilter}
                                            monthCounts={monthCounts}
                                            bulkApplyUrls={bulkApplyUrls}
                                            lastTouchedFilter={lastTouchedFilter['revenue:reconciled']}
                                            bulkApplyHintDismissed={bulkApplyHintDismissed}
                                            onDismissBulkApplyHint={dismissBulkApplyHint}
                                            onBulkApplied={refreshAfterBulkApply}
                                            onBulkApplyResultChange={onBulkApplyResultChange}
                                            archiveActualCost={requestArchive}
                                        />
                                    )}
                                    {showExpenditure && (
                                        <ActualCostsAllocationTable
                                            glCodes={glCodes}
                                            departments={departments}
                                            directLineItems={directLineItems}
                                            data={_.isObject(actualCostsReconciledExpenditure) ? actualCostsReconciledExpenditure : {}}
                                            header={$t('Reconciled Expenditure')}
                                            saveAllocationUrl={saveAllocationUrl}
                                            fetchData={(callback) => fetchData('expense', 1, callback)}
                                            fetchTotals={fetchTotals}
                                            request={requestReconciledExpenditure}
                                            dropAllocationUrl={props.dropAllocationUrl}
                                            types={types}
                                            transactionTypes={transactionTypes}
                                            typeWorkforce={props.typeWorkforce}
                                            typeNonWorkforce={props.typeNonWorkforce}
                                            taxEnabled={props.taxEnabled}
                                            taxes={expenditureTaxes}
                                            updateTaxUrl={props.updateTaxUrl}
                                            setActualCostsItem={(month, index, data) => setActualCostsItem('reconciled', 'expenditure', month, index, data)}
                                            toggleCollapsed={() => toggleCollapsed('reconciledExpenditure')}
                                            collapsed={collapsed.reconciledExpenditure}
                                            suppliers={suppliers}
                                            storeKey={eventId + '_reconciled_expenditure'}
                                            processActualCostsAfterAllocation={processActualCostsAfterAllocation}
                                            allContracts={allContracts}
                                            refreshUrl={combineRefreshUrl('expense')}
                                            pagination={paginationReconciledExpenditure}
                                            setPaginationPage={(page) => setPaginationPage('expense', 1, page)}
                                            setPaginationPerPage={(perPage) => setPaginationPerPage('expense', 1, perPage)}
                                            filters={filtersReconciledExpenditure}
                                            setFilters={(key, value, isFilter) => applyFilters(key, value, isFilter, 'expense', true)}
                                            tableFilters={tableFilters.expense.reconciled}
                                            clearColumnFiltersSignal={clearFiltersSignal['expense:reconciled']}
                                            actualCostType={'expense'}
                                            isReconciled={true}
                                            fetchFilters={fetchFilters}
                                            editableGeneralInfo={editableGeneralInfo}
                                            editActualCostGeneralInfoItem={(month, index, key, value) =>
                                                editActualCostGeneralInfoItem(1, 'expenditure', month, index, key, value)
                                            }
                                            resetEdit={(month, index, oldData) => resetEdit(1, 'expenditure', month, index, oldData)}
                                            saveGeneralInfo={(id, callback) => saveGeneralInfo(1, 'expense', id, callback)}
                                            toast={toast}
                                            confirmActualCosts={confirmActualCosts}
                                            eligibleIdsUrl={props.eligibleIdsUrl}
                                            dateFilter={dateFilter}
                                            glCodeFilter={glCodeFilter}
                                            monthCounts={monthCounts}
                                            bulkApplyUrls={bulkApplyUrls}
                                            lastTouchedFilter={lastTouchedFilter['expense:reconciled']}
                                            bulkApplyHintDismissed={bulkApplyHintDismissed}
                                            onDismissBulkApplyHint={dismissBulkApplyHint}
                                            onBulkApplied={refreshAfterBulkApply}
                                            onBulkApplyResultChange={onBulkApplyResultChange}
                                            archiveActualCost={requestArchive}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        {/* Send for commitment tab — the editable commitment queue */}
                        <div id="reconciliation-tab-send-for-commitment" role="tabpanel" hidden={activeTab !== TAB_SEND_FOR_COMMITMENT}>
                            {activeTab === TAB_SEND_FOR_COMMITMENT && !sendForCommitmentRequest && sendForCommitmentItems.length === 0 ? (
                                <EmptyState
                                    tab={TAB_SEND_FOR_COMMITMENT}
                                    filterActive={pageLevelFilterActive}
                                    // The unfiltered figure is never fetched for this tab, so a
                                    // narrowed and empty view says so without claiming a number
                                    // it does not have — see EmptyState's `hiddenCount` contract.
                                    // Only the page-level Month / GL Code selection reaches this
                                    // list; it carries no column filters of its own.
                                    hiddenCount={pageLevelFilterActive ? null : 0}
                                    onClearFilters={resetFilters}
                                />
                            ) : (
                                <SendForCommitmentQueue
                                    items={sendForCommitmentItems}
                                    loading={sendForCommitmentRequest}
                                    listUsersUrl={props.listUsersUrl}
                                    currentUserId={props.currentUserId}
                                    submitUrl={props.submitForCommitmentUrl}
                                    // fetchTotals, not fetchSendForCommitment: it calls the listing
                                    // fetch itself, so the rows and the tab badge counting them
                                    // settle together rather than one refresh apart.
                                    onSubmitted={fetchTotals}
                                />
                            )}
                        </div>

                        {/* Archived tab — read-only, Restore is the only action */}
                        <div id="reconciliation-tab-archived" role="tabpanel" hidden={activeTab !== TAB_ARCHIVED}>
                            {activeTab === TAB_ARCHIVED && isArchivedTabEmpty ? (
                                <EmptyState
                                    tab={TAB_ARCHIVED}
                                    filterActive={columnFiltersActiveOnTab(SLOT_ARCHIVED) || pageLevelFilterActive}
                                    hiddenCount={hiddenCountFor(SLOT_ARCHIVED, (baseline) => ({
                                        revenue: baseline.archivedTotals?.revenue?.number,
                                        expenditure: baseline.archivedTotals?.expenditure?.number,
                                    }))}
                                    onClearFilters={() => clearAllFilters(SLOT_ARCHIVED)}
                                />
                            ) : (
                                <>
                                    <ArchivedSummary totals={archivedTotals} />
                                    {showRevenue && (
                                        <ArchivedActualCostsTable
                                            header={$t('Archived Revenue')}
                                            data={_.isObject(actualCostsArchivedRevenue) ? actualCostsArchivedRevenue : {}}
                                            request={requestArchivedRevenue}
                                            departments={departments}
                                            directLineItems={directLineItems}
                                            allContracts={allContracts}
                                            transactionTypes={transactionTypes}
                                            storeKey={eventId + '_archived_revenue'}
                                            glCodes={glCodes}
                                            tableFilters={tableFilters.revenue.archived}
                                            clearColumnFiltersSignal={clearFiltersSignal['revenue:archived']}
                                            pagination={paginationArchivedRevenue}
                                            setPaginationPage={(page) => setPaginationPage('revenue', SLOT_ARCHIVED, page)}
                                            setPaginationPerPage={(perPage) => setPaginationPerPage('revenue', SLOT_ARCHIVED, perPage)}
                                            filters={filtersArchivedRevenue}
                                            setFilters={(key, value, isFilter) => applyFilters(key, value, isFilter, 'revenue', SLOT_ARCHIVED)}
                                            collapsed={collapsed.archivedRevenue}
                                            toggleCollapsed={() => toggleCollapsed('archivedRevenue')}
                                            onRestore={restoreActualCost}
                                            restoringIds={restoringIds}
                                        />
                                    )}
                                    {showExpenditure && (
                                        <ArchivedActualCostsTable
                                            header={$t('Archived Expenditure')}
                                            data={_.isObject(actualCostsArchivedExpenditure) ? actualCostsArchivedExpenditure : {}}
                                            request={requestArchivedExpenditure}
                                            departments={departments}
                                            directLineItems={directLineItems}
                                            allContracts={allContracts}
                                            transactionTypes={transactionTypes}
                                            storeKey={eventId + '_archived_expenditure'}
                                            glCodes={glCodes}
                                            tableFilters={tableFilters.expense.archived}
                                            clearColumnFiltersSignal={clearFiltersSignal['expense:archived']}
                                            pagination={paginationArchivedExpenditure}
                                            setPaginationPage={(page) => setPaginationPage('expense', SLOT_ARCHIVED, page)}
                                            setPaginationPerPage={(perPage) => setPaginationPerPage('expense', SLOT_ARCHIVED, perPage)}
                                            filters={filtersArchivedExpenditure}
                                            setFilters={(key, value, isFilter) => applyFilters(key, value, isFilter, 'expense', SLOT_ARCHIVED)}
                                            collapsed={collapsed.archivedExpenditure}
                                            toggleCollapsed={() => toggleCollapsed('archivedExpenditure')}
                                            onRestore={restoreActualCost}
                                            restoringIds={restoringIds}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            <ArchiveTransactionModal modal={archiveModal} row={rowToArchive} onConfirm={submitArchive} archiving={archiving} />
        </div>
    );
}

if (document.getElementById('reconciliationPage')) {
    const rootEl = document.getElementById('reconciliationPage');
    const dashboardUrl = rootEl.getAttribute('data-dashboard-url');
    // Read from the mount element rather than a Blade global: the Send for
    // commitment queue is the only consumer, and a data attribute cannot go
    // missing in one of the views that mount this page.
    const listUsersUrl = rootEl.getAttribute('data-list-users-url');
    const submitForCommitmentUrl = rootEl.getAttribute('data-submit-for-commitment-url');
    const currentUserId = Number(rootEl.getAttribute('data-current-user-id')) || null;
    const uploadHistoryUrl = rootEl.getAttribute('data-upload-history-url');
    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <ReconciliationIndex
                    dashboardUrl={dashboardUrl}
                    indexUrl={reconciliationIndexUrl}
                    totalsUrl={reconciliationTotalsUrl}
                    sendForCommitmentUrl={reconciliationSendForCommitmentUrl}
                    listUsersUrl={listUsersUrl}
                    submitForCommitmentUrl={submitForCommitmentUrl}
                    currentUserId={currentUserId}
                    updateTaxUrl={updateTaxUrl}
                    locationsUrl={locationsUrl}
                    filtersUrl={filtersUrl}
                    refreshUrl={refreshUrl}
                    saveAllocationUrl={saveAllocationUrl}
                    dropAllocationUrl={dropAllocationUrl}
                    typeWorkforce={typeWorkforce}
                    typeNonWorkforce={typeNonWorkforce}
                    taxEnabled={taxEnabled}
                    editableGeneralInfo={editableGeneralInfo}
                    updateGeneralDataUrl={updateGeneralDataUrl}
                    uploadUrl={uploadUrl}
                    uploadHistoryUrl={uploadHistoryUrl}
                    deleteAllUrl={deleteAllUrl}
                    isFileRequired={isFileRequired}
                    integrationConfigured={integrationConfigured}
                    syncEnabled={syncEnabled}
                    action={action}
                    model={model}
                    toggleUrl={toggleUrl}
                    syncNowUrl={syncNowUrl}
                    syncTypes={syncTypes}
                    confirmationUrl={confirmationUrl}
                    eligibleIdsUrl={eligibleIdsUrl}
                    bulkApplyPreviewUrl={bulkApplyPreviewUrl}
                    bulkApplyUrl={bulkApplyUrl}
                    bulkApplyUndoUrl={bulkApplyUndoUrl}
                    archiveUrl={archiveUrl}
                    restoreUrl={restoreUrl}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
